import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { GET_OUTFITS_QUERY } from "@/graphql/queries";
import { UPDATE_OUTFIT_MUTATION, DELETE_OUTFIT_MUTATION } from "@/graphql/mutations/outfit";
import { OutfitItem } from "@/types/OutfitItem";
import { useState, useEffect, useRef } from "react";
import {
  SparklesIcon,
  HeartIcon,
  ShareIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  ArrowLeftIcon,
  ChatBubbleLeftEllipsisIcon,
  UserCircleIcon,
  EyeIcon,
  XMarkIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { classNames } from "@/components/utils/functions";
import ChatInterface from "@/components/wardrobe/ChatInterface";
import { useOutfitImageGeneration } from "@/hooks/useOutfitImageGeneration";
import OutfitImageGenerationStatus from "@/components/ui/OutfitImageGenerationStatus";

interface ChatMessage {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  action?: "generate_outfit" | "update_tags" | "update_colors";
}

export default function OutfitDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // State for chat interface
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  // Real-time image generation tracking
  const { status: imageGenStatus, isGenerating: isGeneratingImage, startGeneration } = useOutfitImageGeneration(id || null);

  // State for modal
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    GET_OUTFITS_QUERY,
    { fetchPolicy: "cache-and-network" }
  );
  const outfit = data?.getCurrentUser?.outfits?.find((o: any) => o.id === id);
  
  // Start generation tracking when outfit is newly created without an image
  useEffect(() => {
    if (outfit && !outfit.imageUrl && !isGeneratingImage) {
      startGeneration(outfit.id);
    }
  }, [outfit, startGeneration, isGeneratingImage]);

  // Refetch outfit data when image generation completes
  useEffect(() => {
    if (imageGenStatus?.status === 'completed' && imageGenStatus?.image_url) {
      refetch();
    }
  }, [imageGenStatus, refetch]);

  const [updateOutfit] = useMutation(UPDATE_OUTFIT_MUTATION);
  const [deleteOutfit, { loading: deleting }] = useMutation(DELETE_OUTFIT_MUTATION, {
    refetchQueries: [{ query: GET_OUTFITS_QUERY }],
    awaitRefetchQueries: true,
  });

  // Initialize chat with welcome message and outfit reasonings
  useEffect(() => {
    if (outfit && messages.length === 0) {
      const welcomeMessage = `Hello! I'm your personal stylist assistant. I see you're looking at your ${outfit.occasion} outfit. How can I help you style this today?`;

      const initialMessages: ChatMessage[] = [
        {
          id: "1",
          type: "assistant",
          content: welcomeMessage,
          timestamp: new Date(),
        },
      ];

      // Add existing reasonings from outfit
      if (outfit.reasonings) {
        const reasoningMessages = outfit.reasonings.map(
          (reasoning: any, index: number) => ({
            id: `reasoning-${index}`,
            type:
              reasoning.role === "user"
                ? "user"
                : ("assistant" as "user" | "assistant"),
            content: reasoning.message,
            timestamp: new Date(reasoning.createdAt || Date.now()),
          })
        );
        initialMessages.push(...reasoningMessages);
      }

      setMessages(initialMessages);
    }
  }, [outfit, messages.length]);

  const addMessage = (
    type: "user" | "assistant",
    content: string,
    action?: string
  ) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      type,
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim() || isTyping) return;

    addMessage("user", inputMessage);
    setInputMessage("");
    handleAIResponse(inputMessage);
  };

  const handleAIResponse = async (userMessage: string) => {
    setIsTyping(true);

    try {
      const response = await updateOutfit({
        variables: {
          input: {
            id: outfit.id,
            chatContext: userMessage,
            occasion: outfit.occasion,
            outfitItemIds:
              outfit.outfitItems?.map((item: OutfitItem) => item.id) || [],
          },
        },
      });

      if (response.data?.updateOutfit?.success) {
        // Real-time updates will be handled by ActionCable
        addMessage(
          "assistant",
          "Got it! I'm generating a new outfit image. You'll see real-time updates as it processes…"
        );
        setIsTyping(false);
        // Refetch to get any immediate updates
        refetch();
      }
    } catch (err) {
      console.error("Error sending message:", err);
      addMessage(
        "assistant",
        "I apologize, but I'm having trouble processing your request right now. Please try again."
      );
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const openModal = (item: any) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    if (!id) return;
    const confirmed = window.confirm(
      "Delete this outfit? This action cannot be undone."
    );
    if (!confirmed) return;

    try {
      const res = await deleteOutfit({ variables: { id } });
      const ok = res.data?.deleteOutfit?.success;
      if (ok) {
        navigate("/lookbook");
      } else {
        const msg =
          res.data?.deleteOutfit?.errors?.fullMessages?.join(", ") ||
          "Failed to delete outfit";
        // Using alert for simplicity; page already has Toaster configured if we want to enhance UX
        alert(msg);
      }
    } catch (e: any) {
      alert(e?.message || "Failed to delete outfit");
    }
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isModalOpen]);

  // Handle real-time image generation status updates
  useEffect(() => {
    if (imageGenStatus?.status === 'completed' && imageGenStatus.image_url) {
      addMessage("assistant", "Your outfit image is ready!");
      // Refetch to get the updated image URL from GraphQL
      refetch();
    } else if (imageGenStatus?.status === 'error') {
      addMessage("assistant", `Image generation failed: ${imageGenStatus.message}`);
    }
  }, [imageGenStatus, refetch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto"></div>
          <p className="mt-4 text-sm font-light tracking-wider uppercase text-gray-500 dark:text-gray-400">
            LOADING OUTFIT DETAILS
          </p>
        </div>
      </div>
    );
  }

  if (error || !outfit) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-light tracking-wider text-gray-900 dark:text-white">
            OUTFIT NOT FOUND
          </h2>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {error
              ? `Error: ${error.message}`
              : "This outfit doesn't exist or has been removed."}
          </p>
          <button
            onClick={() => navigate("/lookbook")}
            className="mt-4 px-6 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-light tracking-wider uppercase hover:scale-105 transition-transform duration-200"
          >
            BACK TO LOOKBOOK
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/lookbook")}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="space-y-1">
              <h1 className="text-lg font-extralight tracking-wider uppercase text-gray-900 dark:text-white">
                {outfit.occasion || "OUTFIT DETAILS"}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {outfit.createdAt && (
                  <span>
                    Created {new Date(outfit.createdAt).toLocaleDateString()}
                  </span>
                )}
                {outfit.updatedAt && outfit.createdAt !== outfit.updatedAt && (
                  <span className="ml-2">
                    • Updated {new Date(outfit.updatedAt).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsFavorited(!isFavorited)}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
            >
              {isFavorited ? (
                <HeartIconSolid className="h-5 w-5 text-red-500" />
              ) : (
                <HeartIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
            <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200">
              <ShareIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              aria-label="Delete outfit"
              title="Delete outfit"
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50"
            >
              <TrashIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Outfit Image and Details */}
          <div className="space-y-6" id="left-column">
            {/* Main Image */}
            <div className="aspect-square rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-lg relative">
              {(() => {
                // Determine which image to show
                // Display logic for outfit image - prioritize fresh data from refetch
                const hasCompletedGeneration = imageGenStatus?.status === 'completed' && imageGenStatus?.image_url;
                const hasExistingImage = outfit?.imageUrl;
                const displayImageUrl = hasExistingImage || (hasCompletedGeneration ? imageGenStatus.image_url : null);
                
                // Stop showing loading if we have an image to display
                const shouldShowLoading = isGeneratingImage && !displayImageUrl;
                
                if (displayImageUrl) {
                  // Show the image (either existing or newly generated)
                  return (
                    <img
                      src={displayImageUrl}
                      alt={outfit.occasion}
                      className="w-full h-full object-cover"
                      onLoad={() => {}}
                      onError={() => {}}
                    />
                  );
                } else if (shouldShowLoading) {
                  // Show loading animation during generation
                  return (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-center">
                        <div className="relative w-16 h-16 mx-auto mb-4">
                          <div className="absolute inset-0 border-2 border-gray-200 dark:border-gray-700 rounded-full"></div>
                          <div className="absolute inset-0 border-2 border-transparent border-t-purple-500 rounded-full animate-spin"></div>
                          <div className="absolute inset-2 border-2 border-transparent border-t-pink-500 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                        </div>
                        <h4 className="text-sm font-light tracking-wider uppercase text-gray-900 dark:text-gray-100 mb-2">
                          {imageGenStatus?.status === 'generating' && 'Creating Your Look'}
                          {imageGenStatus?.status === 'downloading' && 'Processing Image'}
                          {!imageGenStatus && 'Preparing Generation'}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400 font-light tracking-wide">
                          {imageGenStatus?.message || 'AI is preparing your outfit visualization...'}
                        </p>
                      </div>
                    </div>
                  );
                } else {
                  // Show placeholder when no image exists
                  return (
                    <div className="w-full h-full flex items-center justify-center">
                      <PhotoIcon className="h-24 w-24 text-gray-300 dark:text-gray-600" />
                    </div>
                  );
                }
              })()}
            </div>

            {/* Real-time Generation Status */}
            {(() => {
              // Use same loading logic as other components
              const hasCompletedGeneration = imageGenStatus?.status === 'completed' && imageGenStatus?.image_url;
              const hasExistingImage = outfit?.imageUrl;
              const displayImageUrl = hasExistingImage || (hasCompletedGeneration ? imageGenStatus.image_url : null);
              const shouldShowLoading = isGeneratingImage && !displayImageUrl;
              
              return shouldShowLoading && (
                <OutfitImageGenerationStatus
                  status={imageGenStatus}
                  isGenerating={shouldShowLoading}
                  className="mt-4"
                />
              );
            })()}

            {/* Outfit Items */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center space-x-2 mb-4">
                <SparklesIcon className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-light tracking-wider uppercase text-gray-900 dark:text-white">
                  OUTFIT PIECES
                </h2>
              </div>

              {outfit.outfitItems?.length ? (
                <div className="grid grid-cols-2 gap-4">
                  {outfit.outfitItems.map((item: any) => (
                    <div
                      key={item.id}
                      className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200 relative group"
                    >
                      {item.imageUrl ? (
                        <div className="relative">
                          <img
                            src={item.imageUrl}
                            alt={item.item}
                            className="w-full h-24 object-cover rounded-lg mb-3"
                          />
                          <button
                            onClick={() => openModal(item)}
                            className="absolute top-2 right-2 p-1 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white dark:hover:bg-gray-800"
                          >
                            <EyeIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-full h-24 bg-gray-200 dark:bg-gray-600 rounded-lg mb-3 flex items-center justify-center">
                          <PhotoIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <div className="text-center">
                        <div className="font-light text-sm text-gray-900 dark:text-white tracking-wide">
                          {item.item}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {item.style}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <PhotoIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No items in this outfit
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Interface */}
          <div className="lg:sticky lg:top-[16rem]">
            <div className="lg:h-[calc(100vh-16rem)]">
              {(() => {
                // Use same loading logic as main image
                const hasCompletedGeneration = imageGenStatus?.status === 'completed' && imageGenStatus?.image_url;
                const hasExistingImage = outfit?.imageUrl;
                const displayImageUrl = hasExistingImage || (hasCompletedGeneration ? imageGenStatus.image_url : null);
                const shouldShowLoading = isGeneratingImage && !displayImageUrl;
                
                return (
                  <ChatInterface
                    messages={messages}
                    inputMessage={inputMessage}
                    setInputMessage={setInputMessage}
                    isTyping={isTyping}
                    generatingOutfit={shouldShowLoading}
                    onSendMessage={handleSendMessage}
                    onKeyDown={handleKeyDown}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Modal for item preview */}
      {isModalOpen && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative max-w-2xl w-full max-h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-light tracking-wider uppercase text-gray-900 dark:text-white">
                  {selectedItem.item}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedItem.style}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {selectedItem.imageUrl ? (
                <div className="aspect-square w-full max-w-md mx-auto rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={selectedItem.imageUrl}
                    alt={selectedItem.item}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-square w-full max-w-md mx-auto rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <PhotoIcon className="h-24 w-24 text-gray-400" />
                </div>
              )}

              {/* Item Details */}
              <div className="mt-6 space-y-3">
                {selectedItem.colors && selectedItem.colors.length > 0 && (
                  <div>
                    <h4 className="text-sm font-light tracking-wider uppercase text-gray-700 dark:text-gray-300 mb-2">
                      COLORS
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.colors.map(
                        (color: string, index: number) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-400"
                          >
                            {color}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                )}

                {selectedItem.category && (
                  <div>
                    <h4 className="text-sm font-light tracking-wider uppercase text-gray-700 dark:text-gray-300 mb-2">
                      CATEGORY
                    </h4>
                    <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-400">
                      {selectedItem.category.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
